import { Leg, PositionData, PositionWithExpiryData, isStrikeData, StrikeData, GetOptionChainResultDataType } from './types';
import { updateGlobalPositions } from '../Datafeeds/datafeed';

// Utility function to convert positions to API format
export const convertPositionsToApiFormat = (
  positions: PositionWithExpiryData,
  symbol: string,
) => {
  const contracts: Record<
    string,
    Record<string, { lots: number; expiry: string; direction: string }>
  > = {};

  // Loop through all expiry dates
  Object.entries(positions).forEach(([expiryDate, expiryPositions]) => {
    Object.values(expiryPositions).forEach((pos: PositionData) => {
      const strike = pos.strike;
      const type = pos.contractType.toLowerCase();
      if (!contracts[strike]) contracts[strike] = {};
      contracts[strike][type] = {
        lots: pos.lots,
        expiry: expiryDate,
        direction: pos.direction === 'long' ? 'buy' : 'sell',
      };
    });
  });

  return {
    positions: {
      symbol: `NSE:${symbol}`,
      contracts,
    },
  };
};

// Function to migrate old localStorage structure to new format
export const migrateLocalStorageStructure = () => {
  const existingData = localStorage.getItem('chartPositions');
  
  if (existingData) {
    try {
      const parsed = JSON.parse(existingData) as Record<string, unknown>;
      
      // Check if it's the old structure (has 'symbol' and 'positions' at root level)
      if (
        typeof parsed.symbol === 'string' && 
        parsed.positions && 
        typeof parsed.positions === 'object' && 
        !parsed.symbols
      ) {
        // Convert old structure to new structure
        const newStructure = {
          symbols: {
            [parsed.symbol]: {
              positions: parsed.positions as Record<string, Record<string, PositionData>>,
              timestamp: typeof parsed.timestamp === 'string' ? parsed.timestamp : new Date().toISOString(),
            }
          }
        };
        
        localStorage.setItem('chartPositions', JSON.stringify(newStructure));
        return true; // Migration successful
      }
    } catch (error) {
      // If parsing fails, clear localStorage and start fresh
      localStorage.removeItem('chartPositions');
    }
  }
  
  return false; // No migration needed or failed
};

// Function to update localStorage with current positions for a specific symbol
export const updateLocalStoragePositions = (
  positions: PositionWithExpiryData,
  symbol: string,
  expiry: string,
) => {
  // Try to migrate old structure first
  migrateLocalStorageStructure();
  
  // Get existing localStorage data
  const existingData = localStorage.getItem('chartPositions');
  let allSymbolsData: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }> = {};

  if (existingData) {
    try {
      const parsed = JSON.parse(existingData) as {
        symbols: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }>;
      };
      allSymbolsData = parsed.symbols || {};
    } catch (error) {
      allSymbolsData = {};
    }
  }

  // Update positions for the current symbol only
  allSymbolsData[symbol] = {
    positions: positions,
    timestamp: new Date().toISOString(),
  };

  // Save the entire symbols object back to localStorage
  localStorage.setItem('chartPositions', JSON.stringify({ symbols: allSymbolsData }));
};

// Function to clear localStorage positions for a specific symbol
export const clearLocalStoragePositions = (
  safeSymbol: string,
  optionTabMetaData: { symbol?: string; optionExpiry?: string } | undefined,
) => {
  // Try to migrate old structure first
  migrateLocalStorageStructure();
  
  const existingData = localStorage.getItem('chartPositions');
  
  if (existingData) {
    try {
      const parsed = JSON.parse(existingData) as {
        symbols: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }>;
      };

      // Remove only the current symbol's data
      if (parsed.symbols && parsed.symbols[safeSymbol]) {
        delete parsed.symbols[safeSymbol];
        
        // If no symbols left, remove the entire localStorage item
        if (Object.keys(parsed.symbols).length === 0) {
          localStorage.removeItem('chartPositions');
        } else {
          // Save the updated symbols data
          localStorage.setItem('chartPositions', JSON.stringify(parsed));
        }
      }
    } catch (error) {
      // If parsing fails, clear localStorage completely
      localStorage.removeItem('chartPositions');
    }
  }
};

// Function to get current LTP for a leg
export const getCurrentLTP = (leg: Leg, optionChainData?: GetOptionChainResultDataType): number => {
  if (!optionChainData?.candles || !leg.strike || !leg.contractType) {
    return leg.ltp;
  }

  const strikeData = optionChainData.candles[leg.strike];
  if (!isStrikeData(strikeData)) {
    return leg.ltp;
  }

  if (leg.contractType === 'CE' && strikeData.CE?.close) {
    return strikeData.CE.close;
  } else if (leg.contractType === 'PE' && strikeData.PE?.close) {
    return strikeData.PE.close;
  }

  return leg.ltp;
};

// Function to calculate MTM for a leg
export const calculateLegMTM = (leg: Leg, optionChainData?: GetOptionChainResultDataType, lotSize = 1): number => {
  const currentLTP = getCurrentLTP(leg, optionChainData);
  const entryPrice = leg.entryPrice || leg.ltp;

  return calculateMTM(
    entryPrice,
    currentLTP,
    leg.lots,
    leg.direction,
    lotSize,
  );
};

// Utility function for MTM calculation
export const calculateMTM = (entryPrice: number, currentLTP: number, lots: number, direction: 'long' | 'short', lotSize = 1): number => {
  if (direction === 'long') {
    return (currentLTP - entryPrice) * lotSize * lots;
  } else {
    return -(currentLTP - entryPrice) * lotSize * lots;
  }
};

// Function to handle leg deletion with position sync
export const handleLegDeleteWithSync = (
  legId: string,
  legs: Leg[],
  positions: PositionWithExpiryData,
  optionTabMetaData: { optionExpiry?: string } | undefined,
  onLegDelete: (legId: string) => void,
  onPositionUpdate: ((positions: PositionWithExpiryData) => void) | undefined,
  safeSymbol: string,
) => {
  onLegDelete(legId);

  const currentExpiry = optionTabMetaData?.optionExpiry;

  if (currentExpiry && positions[currentExpiry]) {
    const legToDelete = legs.find(leg => leg.id === legId);

    if (legToDelete) {
      const positionKey = `${legToDelete.contractType}_${legToDelete.strike}`;

      const updatedPositionsForExpiry = { ...positions[currentExpiry] };
      delete updatedPositionsForExpiry[positionKey];

      const updatedPositions: PositionWithExpiryData = {
        ...positions,
        [currentExpiry]: updatedPositionsForExpiry,
      };

      if (onPositionUpdate) {
        onPositionUpdate(updatedPositions);
      }

      updateLocalStoragePositions(
        updatedPositions,
        safeSymbol,
        currentExpiry,
      );

      const apiFormatObject = convertPositionsToApiFormat(
        updatedPositions,
        safeSymbol,
      );
      updateGlobalPositions(apiFormatObject.positions);
    }
  }
};

// Function to handle clearing all legs with position sync
export const handleClearLegsWithSync = (
  positions: PositionWithExpiryData,
  optionTabMetaData: { optionExpiry?: string } | undefined,
  onClearLegs: () => void,
  onPositionUpdate: ((positions: PositionWithExpiryData) => void) | undefined,
  safeSymbol: string,
) => {
  onClearLegs();

  const currentExpiry = optionTabMetaData?.optionExpiry;

  if (currentExpiry) {
    const updatedPositions: PositionWithExpiryData = {
      ...positions,
      [currentExpiry]: {},
    };

    if (onPositionUpdate) {
      onPositionUpdate(updatedPositions);
    }

    updateLocalStoragePositions(updatedPositions, safeSymbol, currentExpiry);

    const apiFormatObject = convertPositionsToApiFormat(
      updatedPositions,
      safeSymbol,
    );
    updateGlobalPositions(apiFormatObject.positions);
  }
};

// Function to restore positions from localStorage
export const restorePositionsFromLocalStorage = (
  safeSymbol: string,
  optionTabMetaData: { optionExpiry?: string } | undefined,
  onPositionUpdate: ((positions: PositionWithExpiryData) => void) | undefined,
  onLegAdd: ((leg: Leg) => void) | undefined,
  onClearLegs: (() => void) | undefined,
) => {
  if (safeSymbol && optionTabMetaData?.optionExpiry && onPositionUpdate && onLegAdd && onClearLegs) {
    try {
      // Try to migrate old structure first
      migrateLocalStorageStructure();
      
      const storedPositions = localStorage.getItem('chartPositions');

      if (storedPositions) {
        const parsedData = JSON.parse(storedPositions) as {
          symbols: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }>;
        };

        // Get positions for the current symbol
        const symbolData = parsedData.symbols?.[safeSymbol];
        
        if (symbolData && symbolData.positions) {
          const restoredPositions: PositionWithExpiryData = symbolData.positions;

          onPositionUpdate(restoredPositions);
          onClearLegs();

          // Restore legs for all expiries of this symbol
          Object.entries(symbolData.positions).forEach(
            ([expiry, expiryPositions]) => {
              Object.entries(expiryPositions).forEach(
            ([positionKey, positionData]) => {
              const [contractType, strike] = positionKey.split('_');
              const newLeg: Leg = {
                    id: `${contractType}_${strike}_${Date.now()}`,
                direction: positionData.direction,
                    expiry: expiry,
                strike: strike,
                lots: positionData.lots,
                ltp: positionData.premium || 0,
                entryTime:
                  positionData.entryTime || new Date().toISOString(),
                contractType: contractType,
                entryPrice: positionData.premium || 0,
                isActive: true,
                iv: positionData.iv,
                premium: positionData.premium,
                contractDetails: positionData.contractDetails,
              };

              onLegAdd(newLeg);
            },
          );
            },
          );

          const apiFormatObject = convertPositionsToApiFormat(
            restoredPositions,
            safeSymbol,
          );
          updateGlobalPositions(apiFormatObject.positions);
        }
      }
    } catch (error) {
      // If there's an error, just continue without restoring
      // Error handling without console logging
    }
  }
}; 

// Function to check if a symbol has saved positions
export const hasSymbolPositions = (symbol: string): boolean => {
  try {
    migrateLocalStorageStructure();
    
    const existingData = localStorage.getItem('chartPositions');
    if (!existingData) return false;
    
    const parsed = JSON.parse(existingData) as {
      symbols: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }>;
    };
    
    const symbolData = parsed.symbols?.[symbol];
    return !!(symbolData && symbolData.positions && Object.keys(symbolData.positions).length > 0);
  } catch (error) {
    return false;
  }
};

// Function to get positions for a specific symbol
export const getLocalStoragePositionsForSymbol = (symbol: string): PositionWithExpiryData => {
  try {
    migrateLocalStorageStructure();
    
    const existingData = localStorage.getItem('chartPositions');
    if (!existingData) return {};
    
    const parsed = JSON.parse(existingData) as {
      symbols: Record<string, { positions: Record<string, Record<string, PositionData>>; timestamp: string }>;
    };
    
    const symbolData = parsed.symbols?.[symbol];
    return symbolData?.positions || {};
  } catch (error) {
    return {};
  }
}; 